<script setup>
/**
 * CrmMultiSelectSearch
 * Multi-select con búsqueda para el módulo CRM.
 * Compatible con el mismo v-model ({id, name}[]) que usa MultiSelect de Chatwoot,
 * pero con input de búsqueda integrado (usa MultiselectDropdownItems internamente).
 */
import { ref, computed, defineModel } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Seleccionar...',
  },
  inputPlaceholder: {
    type: String,
    default: 'Buscar...',
  },
  noSearchResult: {
    type: String,
    default: 'Sin resultados',
  },
});

const model = defineModel({ type: Array, required: true });
const isOpen = ref(false);

const toggle = () => { isOpen.value = !isOpen.value; };
const close  = () => { isOpen.value = false; };

const selectedIds = computed(() =>
  (model.value || []).map(item => (typeof item === 'object' ? item.id : item))
);

const selectedItems = computed(() =>
  props.options.filter(o => selectedIds.value.includes(o.id))
);

const onSelect = option => {
  const current = model.value || [];
  const alreadySelected = selectedIds.value.includes(option.id);
  if (alreadySelected) {
    model.value = current.filter(item =>
      (typeof item === 'object' ? item.id : item) !== option.id
    );
  } else {
    model.value = [...current, { id: option.id, name: option.name }];
  }
  // ⬆ No cerramos el dropdown — el usuario puede seguir seleccionando
};
</script>

<template>
  <OnClickOutside @trigger="close">
    <div class="relative w-full">
      <!-- Trigger button -->
      <button
        type="button"
        class="w-full flex items-center justify-between gap-2 px-3 py-2 border border-n-weak rounded-lg bg-white dark:bg-n-solid-2 text-sm text-n-slate-12 hover:border-n-strong focus:outline-none focus:ring-2 focus:ring-woot-500 min-h-[2.25rem]"
        @click="toggle"
      >
        <span v-if="selectedItems.length === 0" class="text-n-slate-11 truncate">
          {{ placeholder }}
        </span>
        <span v-else class="flex flex-wrap gap-1 flex-1 min-w-0">
          <span
            v-for="item in selectedItems"
            :key="item.id"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-n-alpha-2 text-n-slate-12 text-xs"
          >
            {{ item.name }}
          </span>
        </span>
        
        <!-- Chevron icon (igual que Select.vue) -->
        <svg 
          class="w-4 h-4 text-n-slate-11 transition-transform duration-200 flex-shrink-0" 
          :class="{ 'rotate-180': isOpen }"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <!-- Dropdown -->
      <div
        v-if="isOpen"
        class="absolute z-[9999] w-full mt-1 border border-n-strong rounded-lg bg-n-alpha-3 backdrop-blur-[100px] shadow-lg p-2 before:content-[''] before:absolute before:inset-0 before:rounded-lg before:backdrop-contrast-70 before:backdrop-blur-sm before:z-0 [&>*]:relative"
      >
        <MultiselectDropdownItems
          :options="options"
          :selected-items="selectedItems"
          :has-thumbnail="false"
          :input-placeholder="inputPlaceholder"
          :no-search-result="noSearchResult"
          @select="onSelect"
        />
      </div>
    </div>
  </OnClickOutside>
</template>
